import type { EnrichmentRunConfig } from '~/features/content-enrichment/types/enrichment-run-config';
import type { CostGate, PlannedCost } from '~/features/cost-estimate/types/cost-estimate.types';
import type { ArchiveHistoryEntry } from '~/features/list-archive/types/list-archive.types';
import type { DimensionScore } from '~/features/trial/types/trial.types';
import type { ProductDetailAttribute, ProductOptimization } from '~/types/enrichment';
import type { SourceShippingAttributes } from '~/types/source-shipping';
import type { AttributeCategorySize } from '~/utils/popular-attributes';

import type { BatchStatus as BatchRetryStatus } from './batch-retry.api.types';
import type { LastActionTypeWire } from './catalog-operations.api.types';

// ---------------------------------------------------------------------------
// Catalog Operations
// ---------------------------------------------------------------------------

/** A single category of products requiring attention (e.g. "conflicting input data"). */
export interface AttentionCategory {
  /** Number of products in this attention category. */
  count: number;
  /** Human-readable description of the category. */
  description: string;
}

/** Props for CatalogAlertBanner. */
export interface CatalogAlertBannerProps {
  /** Total number of products flagged for attention across all categories. */
  totalCount: number;
  /** Breakdown of attention categories shown inline in the banner. */
  categories: AttentionCategory[];
  /** Called when the user clicks "View All". */
  onViewAll?: () => void;
}

/** Direction of a metric trend indicator. */
export type TrendDirection = 'up' | 'down' | 'none';

/** Trend indicator shown below a stat card value. */
export interface StatTrend {
  /** Descriptive label for the trend (e.g. "8.2% vs last week"). */
  label: string;
  /** Arrow direction. Use "none" when no directional change applies. */
  direction: TrendDirection;
  /**
   * Whether the trend is considered positive (green) or negative (red).
   * Note: "down" can be positive (e.g. latency improvement).
   */
  positive: boolean;
}

/** Mapped listing-page stats — UI shape returned by `useCatalogStats`. */
export interface CatalogStats {
  batchesThisWeek: number;
  /** 0–1 float (e.g. 0.92 ⇒ 92%). */
  successRate: number;
  totalProductsDelivered: number;
  /** 0–1 float; signed (negative ⇒ regression).
   *  `null` when the backend has no last-week data to compare against. */
  totalProductsDeliveredDelta: number | null;
  /** Average processing latency, in milliseconds. */
  avgProcessingLatencyMs: number;
  /** 0–1 float; signed (negative ⇒ improvement). */
  avgProcessingLatencyDelta: number;
  /** Total products flagged for attention across categories. */
  needsAttentionTotal: number;
  /** Two-bucket breakdown returned by the backend (Critical + Warning). */
  needsAttentionCategories: AttentionCategory[];
}

/** Per-connector aggregate (total / valid / needs-attention) returned by
 *  `GET /curation/stats`. */
export interface CurationStatsBucket {
  total: number;
  valid: number;
  needsAttention: number;
}

/** Curation stats payload powering the alert banner on the catalog
 *  operations page. `perSource` is keyed by connector code so the UI can
 *  surface per-connector breakdowns when needed. */
export interface CurationStats extends CurationStatsBucket {
  subsidiaryId: string;
  connectorCode: string;
  perSource: Record<string, CurationStatsBucket>;
}

/** Props for CatalogStatCard. */
export interface CatalogStatCardProps {
  /** Column label shown above the metric value. */
  label: string;
  /** The primary metric displayed prominently. */
  value: string;
  /** Optional trend indicator shown below the value. */
  trend?: StatTrend;
  /** When provided, the entire card becomes interactive and triggers this handler. */
  onClick?: (() => void) | undefined;
  /** When true, renders a crown badge next to the label to indicate a premium-only metric. */
  isPremium?: boolean;
}

// ---------------------------------------------------------------------------
// Products page
// ---------------------------------------------------------------------------

/** Tab identifier on the products drill-in page. */
export type ProductTab = 'newly-enriched' | 'needs-attention';

/** Mirrors `CurationFilterType` from the API. */
export type CurationFilterType =
  | 'multi_select'
  | 'text_contains'
  | 'text'
  | 'boolean'
  | 'numeric_range'
  | 'date_range';

/** UI-shape filter definition consumed by `ProductFilterDrawer`. Wire field
 *  names match the backend so the mapper stays trivial. */
export interface CurationFilterDefinition {
  key: string;
  label: string;
  type: CurationFilterType;
  /** Pre-populated option strings (multi_select only). */
  options: string[];
  /** Run-scoped filters only: products carrying each option (multi_select). */
  optionCounts?: Record<string, number>;
  /** Run-scoped filters only: the option list was capped server-side. */
  optionsTruncated?: boolean;
  /** Range types only: the run's lower/upper bound — a number for
   *  `numeric_range`, an ISO 8601 string for `date_range`. The drawer writes
   *  the user's bounds to `<key>__min` / `<key>__max`. */
  min?: number | string | null;
  max?: number | string | null;
}

/** Effective filter value map sent to the products listing endpoint.
 *  `string[]` carries multi-select selections (comma-joined by the service),
 *  `string` carries free-text inputs (text / text_contains), and `boolean`
 *  carries toggles. */
export type ProductFilterValues = Record<string, string | string[] | boolean>;

/** One extra attribute a row carries beyond the fixed `Product` fields. The
 *  content-enrichment listing builds its toggleable columns from these, so the
 *  column set follows whatever the enrichment response actually returned rather
 *  than a hardcoded list. `key` is a stable camelCase column id, `label` the
 *  header, `value` the display string. */
export interface EnrichedProductAttribute {
  key: string;
  label: string;
  value: string;
  /** Raw JSON-shaped value (before it was stringified into `value`) — the shape
   *  the attribute PATCH expects. Scalars keep their string; structured fields
   *  carry their array/object. Set only by the content-enrichment listing for
   *  its editable grid; absent on the catalog-operations page. */
  raw?: unknown;
  /** How the editable grid edits this field (`text` inline, `textarea`/`json`
   *  in a modal). Absent → not editable / read-only. */
  editor?: 'text' | 'textarea' | 'json';
  /** snake_case wire field name the attribute PATCH is keyed by (e.g.
   *  `product_type`). Absent on non-editable attributes. */
  wireField?: string;
}

/** Which enrichment pass produced an enriched row. The enriched-products reads span
 *  both scopes (sample ∪ pipeline), so a full-catalog listing can hold `SAMPLE` rows
 *  when the full pass had nothing new to curate. Origin only — both passes emit
 *  identical content for the same SKU. */
export type EnrichmentPhase = 'SAMPLE' | 'FULL';

/** A single product row on the products page. */
export interface Product {
  id: string;
  /** Source batch — used to build the product detail link from any list view. */
  batchId: string;
  title: string;
  /** Retailer's own product identifier, shown below the title in list view. */
  sourceProductId: string;
  imageUrl?: string;
  tags: string[];
  trends: string[];
  sku: string;
  brand: string;
  category: string;
  /** Extra response attributes surfaced as dynamic, toggleable columns by the
   *  content-enrichment listing. Empty/omitted for the catalog-operations page,
   *  which uses the fixed columns above. */
  enrichedAttributes?: readonly EnrichedProductAttribute[];
  // --- Fields the curation row already carries. All optional so existing
  // construction sites (fixtures, the needs-attention list) stay valid, and so a
  // payload that omits one renders as absent rather than as a wrong value. ---
  /** GMC `gender` — rendered with `ageGroup` as a single audience phrase. */
  gender?: string | undefined;
  /** GMC `age_group` (`adult` | `kids` | …). */
  ageGroup?: string | undefined;
  /** Every size on the variant group. Displayed as a range, not a list. */
  sizes?: string[] | undefined;
  color?: string | undefined;
  material?: string | undefined;
  pattern?: string | undefined;
  /** Enriched product description — surfaced as an optional list column. */
  description?: string | undefined;
  /** Enriched product-type path — surfaced as an optional list column. */
  productType?: string | undefined;
  /** Enriched Google Product Category taxonomy path — optional list column. */
  googleProductCategory?: string | undefined;
  /** Enriched structured-title text (the `content` of the structured object) — optional list column. */
  structuredTitle?: string | undefined;
  /** Enriched structured-description text (the `content` of the structured object) — optional list column. */
  structuredDescription?: string | undefined;
  /** Enriched product highlights, joined into one line — optional list column. */
  productHighlight?: string | undefined;
  /** Enriched product-detail attributes, summarised into one line — optional list column. */
  productDetail?: string | undefined;
  /** Curation completeness, 0–100. Catalog coverage (are fields populated), NOT
   *  enrichment quality. Mapped from the wire but **deliberately not rendered**:
   *  it is near-constant across a feed, so as a "Score" column it looked
   *  meaningful while telling an operator nothing actionable. Don't reinstate it
   *  as a score — see `scoreBadge`. */
  completenessScore?: number | undefined;
  /** Post-enrichment quality score, 0–100. Paired with {@link preEnrichmentScore}
   *  to render the lift indicator, and the ONLY source the score column and tile
   *  badge accept. Optional because the curation list endpoint does not carry it
   *  yet — until it does, neither surface shows a score at all. */
  enrichmentScore?: number | undefined;
  /** Pre-enrichment quality score on the same 0–100 scale as
   *  {@link enrichmentScore}. Only meaningful alongside it — one half on its own
   *  yields no delta, so the tile renders the plain score instead of a lift. */
  preEnrichmentScore?: number | undefined;
  /** Raw amount as returned; formatted at the render site with `currency`. */
  price?: string | undefined;
  currency?: string | undefined;
  /** Distinct images available, for the tile's image-count badge. */
  imageCount?: number | undefined;
  /** `is_new` on the curation run — flags first-time rows on a repeat catalogue. */
  isNew?: boolean | undefined;
  /** Why the row is flagged. Only meaningful on the needs-attention tab. */
  needsAttentionReason?: string | undefined;
  /** Whether enrichment actually produced output for this product. Merged in from
   *  `enrichments/products/enrichment-status`, not the curation row, so it is
   *  `undefined` until that call lands — only an explicit `false` badges the row. */
  isEnriched?: boolean | undefined;
  /** Which enrichment pass produced this row (`FULL` when the wire omits it). Set
   *  only by the content-enrichment listing adapter for full-catalog rows; catalog
   *  rows and sample-step rows leave it undefined, which renders no badge. */
  enrichmentPhase?: EnrichmentPhase | undefined;
  // --- Versioning. Undefined until the curation service ships it, which is why
  // every consumer must treat "absent" as "no version information", not as v0.
  /** Version the live feed row holds today. */
  version?: number | undefined;
  /** Version THIS RUN saw. Lower than `version` ⇒ the run processed data now in
   *  history; that comparison is the whole staleness signal. */
  feedProductVersion?: number | undefined;
  /** Internal-product version the run left this pointing at. Null on the wire
   *  means load never reached it in that run — not that it has none. */
  productVersion?: number | undefined;
}

/** True when a run processed older data than the live row now holds — the one
 *  comparison the versioning work exists to enable. False when either number is
 *  missing: absent version data is "unknown", never "stale". */
export function isRecuratedSince(product: Product): boolean {
  const { feedProductVersion, version } = product;
  if (feedProductVersion === undefined || version === undefined) return false;
  return feedProductVersion < version;
}

/** One entry in a product's version timeline. */
export interface ProductVersionEntry {
  version: number;
  /** Null on rows predating versioning — render `createdAt` instead of
   *  inventing a date. */
  validFrom: string | null;
  validTo: string | null;
  isCurrent: boolean;
  createdByRunId: string | null;
  supersededByRunId: string | null;
  changeSource: string | null;
  /** Fields that differ from the NEXT OLDER version, computed server-side.
   *  Empty on the oldest entry. */
  changedFields: string[];
  /** Preview columns the feed timeline carries inline. Absent on the internal
   *  timeline, which describes a different entity. */
  title?: string | null | undefined;
  brand?: string | null | undefined;
}

/**
 * A product's full version timeline.
 *
 * `currentVersion` says where the live row sits; `totalVersions` says how many
 * are held. They match today and diverge if history is ever pruned — keep them
 * apart rather than deriving one from the other.
 */
export interface ProductVersionTimeline {
  currentVersion: number;
  totalVersions: number;
  entries: ProductVersionEntry[];
}

/**
 * One archived version's content, as a flat field map.
 *
 * Deliberately NOT a `ProductDetail`: the endpoint returns the version's own
 * columns with no id, no enrichment, no runs. Rendering it through the detail
 * component would imply data the payload does not carry.
 */
export interface ProductVersionSnapshot {
  version: number;
  isCurrent: boolean;
  validFrom: string | null;
  createdByRunId: string | null;
  fields: Record<string, unknown>;
}

/** Filter-aware tab counts emitted alongside the products payload. Values
 *  reflect the currently-applied filter set so the UI can use them as live
 *  badges on the Newly Enriched / Needs Attention tabs. */
export interface ProductCounts {
  /** All matching rows (across newly-enriched + needs-attention). */
  total: number;
  /** Rows in the newly-enriched bucket. */
  newlyEnriched: number;
  /** Rows flagged as needing attention. */
  needsAttention: number;
}

/** Paginated result returned by the products endpoint. */
export interface ProductsResult {
  products: Product[];
  total: number;
  /** Present when the API returned a `counts` block (newer payloads).
   *  Older responses omit this — consumers should fall back to hiding the
   *  count badge rather than rendering 0. */
  counts?: ProductCounts;
}

// Re-exported from ~/types/enrichment for backward compatibility.
// Import directly from '~/types/enrichment' in new code.
export type {
  OptimizationContentType,
  OptimizationImpact,
  ProductDetailAttribute,
  ProductOptimization,
} from '~/types/enrichment';

/** Read-through lifecycle for the per-product gap read. `COMPLETED` with a null
 *  summary is a legitimate "no distinct attributes" answer, not still-loading. */
export type GapAnalysisStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

/** Which side of the comparison an attribute came from: the retailer's original
 *  listing (`baseline`) or Lily's enrichment (`enriched`). */
export type GapSource = 'baseline' | 'enriched';

/** Volume-lookup outcome. Anything other than `ok` means only the search-volume
 *  figures are unreliable — the net-new vs restated delta still holds. */
export type GapVolumeStatus =
  | 'ok'
  | 'ok_semantic_exact'
  | 'unconfigured'
  | 'no_subsidiary'
  | (string & {});

/** One descriptive attribute in the gap analysis. `isNetNew` is null on
 *  `baseline` rows — the verdict only applies to the enriched side, where true
 *  means genuinely new information and false a semantic restatement. */
export interface GapAttribute {
  tag: string;
  source: GapSource;
  isNetNew: boolean | null;
  searchVolume: number | null;
  competitionLevel: string | null;
  matchedTerm: string | null;
}

/** One section bucket of gap rows. `section` is a cosmetic grouping label only. */
export interface GapAnalysisSection {
  section: string;
  rows: GapAttribute[];
}

/** Per-product gap rollup. Non-empty `issues` means the counts under-represent
 *  the product's content and should be surfaced with a caveat. */
export interface GapAnalysisSummary {
  baselineAttributeCount: number;
  enrichedAttributeCount: number;
  netNewCount: number;
  restatedCount: number;
  netNewSearchVolume: number;
  /** Null when the run didn't record which threshold produced these verdicts. */
  similarityThreshold: number | null;
  volumeStatus: GapVolumeStatus;
  issues: string[];
}

/** Content-delta gap analysis for one product. `summary` is null and `sections`
 *  empty when the product has no persisted gap data — a legitimate result. */
export interface GapAnalysisProduct {
  productId: string;
  /** Read-through lifecycle — drives polling and the failed state. */
  status: GapAnalysisStatus;
  /** Reason to surface; non-null only when `status` is `FAILED`. */
  error: string | null;
  summary: GapAnalysisSummary | null;
  sections: GapAnalysisSection[];
}

/** Outcome of a search-descriptor or phrase-description strategy run. Open-ended
 *  arm because the backend enum can grow without an FE release, so any renderer
 *  must handle an unrecognised value. */
export type SearchDescriptorStatus =
  | 'applied'
  | 'llm_generated'
  | 'no_tag_breakdown'
  | 'error'
  | 'unknown'
  | (string & {});

/** Where the phrase pool came from. Anything other than `qdrant` means the model
 *  invented the phrases off-pool, so the volume figures carry a caveat — the
 *  phrases themselves are still the run's real output. */
export type SearchDescriptorKeywordSource = 'qdrant' | (string & {});

/** One phrase under a pivot. `searchVolume` null = off-pool phrase, not an error. */
export interface AssociatedPhrase {
  phrase: string;
  phraseRank: number;
  searchVolume: number | null;
}

/** One pivot and its associated phrases, both already rank-ordered by the server. */
export interface SearchPivot {
  pivotPhrase: string;
  pivotRank: number;
  searchVolume: number | null;
  associatedPhrases: AssociatedPhrase[];
}

/** The agent's self-rating. Null on rows written before it recorded one. */
export interface SearchDescriptorAccuracy {
  /** 0–1. */
  score: number;
  reasoning: string;
}

/** Three progressively longer descriptions built from the product's search
 *  phrases. Any level can be null while the others succeeded. */
export interface PhraseDescription {
  /** Null when the row exists but the strategy produced nothing. */
  status: SearchDescriptorStatus | null;
  inputDescription: string | null;
  inputDescriptionSource: string | null;
  level1Description: string | null;
  level2Description: string | null;
  level2Base: string | null;
  level3Description: string | null;
  /** Non-null only when level 3 specifically failed. */
  level3Error: string | null;
  /** Opaque agent JSON — typed `unknown` so no consumer can assume a shape. */
  levelsProduced: unknown;
  /** Opaque agent JSON — typed `unknown` so no consumer can assume a shape. */
  phrasesUsed: unknown;
  generatedAt: string | null;
}

/** Search descriptors + phrase descriptions for one product's latest run.
 *
 *  The two halves are independent and must be branched separately: empty
 *  `searchPhrases` means no search descriptors, null `phraseDescription` means
 *  that strategy never ran. The run-level fields describe the search-descriptor
 *  run only, and are all null when just phrase descriptions ran. */
export interface ProductSearchDescriptors {
  productId: string;
  workflowId: string | null;
  phase: string | null;
  status: SearchDescriptorStatus | null;
  keywordSource: SearchDescriptorKeywordSource | null;
  pivotCount: number | null;
  phraseCount: number | null;
  accuracy: SearchDescriptorAccuracy | null;
  searchPhrases: SearchPivot[];
  phraseDescription: PhraseDescription | null;
  generatedAt: string | null;
}

/** Full product detail payload. The overview-only fields come from
 *  `GET /curation/products/:productId`; `enrichmentScore` and `optimizations`
 *  are populated by a separate enrichment endpoint that is not yet wired —
 *  the page hides those sections while they're absent. */
export interface ProductDetail {
  id: string;
  title: string;
  /** Retailer's own product identifier. */
  retailerProductId: string;
  /** Ordered list of image URLs for the gallery carousel. */
  images: string[];
  lilyId: string;
  /**
   * The INTERNAL (curated) product id — `product.id` on the payload, and the
   * only correct input to the internal-products endpoints.
   *
   * Deliberately separate from `lilyId`, which falls back to the feed product id
   * when the nested product is absent. Passing a feed id to an internal-product
   * route returns another entity's history or nothing at all, and looks
   * plausible either way. Undefined when the row has no linked product yet.
   */
  internalProductId?: string | undefined;
  sku: string;
  brand: string;
  retailerCategory: string;
  tags: string[];
  trends: string[];
  deliveryDate: string;
  attributeCount: number;
  trendCount: number;
  highlightCount: number;
  searchDescriptorCount: number;
  sourcePlatform: string;
  destinationPlatform: string;
  status: 'confirmed' | 'needs-attention' | 'failed';
  /** Populated by the enrichment endpoint (separate API). */
  enrichmentScore?: {
    before: number;
    after: number;
    delta: number;
  };
  /** Populated by the enrichment endpoint (separate API). */
  optimizations?: ProductOptimization[];
  /** Normalized, display-ready snapshot of raw scalar/array/object fields
   *  from the curation product detail response. Keyed by curation field name
   *  (e.g. `title`, `product_highlights`, `extra_attributes`). Used to hydrate
   *  the "Current" panel in the optimization accordion. */
  rawFields?: Record<string, string>;
  /** Structured product attributes from the curation product detail response.
   *  Populated only when the API returns the new object-array shape for
   *  `product_details`; undefined for the legacy string/string[] shapes. */
  productDetailsStructured?: ProductDetailAttribute[];
  /** Raw `feed_source` value from the curation API (e.g. `META_COMMERCE`,
   *  `GOOGLE_MERCHANT`). Used to derive the `connectorCode` for the enrichment
   *  endpoint. Undefined when the API omits the field. */
  feedSource?: string | null;
  /** GMC merchant-feed shipping / logistics / physical attributes from the curation
   *  detail. Undefined when the feed states none of them (always for Meta/Shopify). */
  sourceShipping?: SourceShippingAttributes;
  /** Populated by the enrichment endpoint (separate API). Platform-native
   *  resource name; trial/demo products surface as `accounts/trial/...`. */
  externalId?: string;
  /** Populated by the enrichment endpoint (separate API). The run that produced
   *  the values on screen — compared against an attribute edit's own
   *  `workflowId` to tell a live override from one a re-run has since reverted. */
  workflowId?: string;
  /** Populated by the enrichment endpoint (separate API). ISO timestamp of when
   *  this product was enriched — surfaced as the "Enriched" date-time on the
   *  product detail overview. */
  enrichedAt?: string;
  /** Populated by the batch-scoped enrichment read. `false` = the batch the page was opened from
   *  never enriched this product; the content on screen is another batch's (see `enrichedAt`). */
  inBatch?: boolean;
  /**
   * Populated by the enrichment endpoint. Which google-agent retailer prompt rendered for this
   * product; absent when the run named no retailer. `fellBack` = the shared prompt was used
   * because the retailer ships none for this product's vertical.
   */
  retailerPrompt?: {
    retailer: string;
    bucket: string;
    resolvedPromptPath: string;
    fellBack: boolean;
  };
  /** Version the live feed row holds. Undefined until curation ships versioning. */
  version?: number | undefined;
  /** How many versions exist — NOT the same as `version`. Render counts from
   *  here and position from `version`; they diverge if history is pruned. */
  totalVersions?: number | undefined;
}

/** Lifecycle of an on-demand data-score. `PENDING` is the only non-terminal
 *  state; every other value stops the poll. `FAILED` means the run closed with
 *  no POST — surface a retry that re-scores with `overrideCache: true`. */
export type OnDemandScoreStatus =
  | 'SCORED'
  | 'ALREADY_SCORED'
  | 'PENDING'
  | 'NOT_ENRICHED'
  | 'FAILED';

/** POST-only per-field deliverability verdict on the enriched value. */
export type DeliveryCheck = 'PASS' | 'minor' | 'major' | 'fail' | 'critical';

/** One side (PRE "before" / POST "after") of an on-demand score. Scores are
 *  0–10 — the same axis as `enrichmentScore`, but sourced from the dedicated
 *  on-demand endpoint rather than the legacy per-product read. */
export interface OnDemandScoreSide {
  overallScore: number;
  dataLabel?: string | undefined;
  correctness?: number | undefined;
  completeness?: number | undefined;
  compliance?: number | undefined;
  relevance?: number | undefined;
  differentiation?: number | undefined;
  /** POST only. */
  titleDeliveryCheck?: DeliveryCheck | undefined;
  /** POST only. */
  descriptionDeliveryCheck?: DeliveryCheck | undefined;
  /** POST only — agent recommendation to re-enrich. */
  routeToReEnrichment?: boolean | undefined;
}

/** Result of an on-demand score/re-score (POST) or poll (GET). `pre`/`post` are
 *  independently nullable; render whichever side is present. */
export interface OnDemandScoreResult {
  productId: string;
  externalId?: string | undefined;
  status: OnDemandScoreStatus;
  /** True when this call ran a fresh scoring pass (vs returning existing). */
  reScored: boolean;
  /** True when the scoring bypassed the caches (`overrideCache`). */
  cacheBypassed: boolean;
  /** `post.overallScore − pre.overallScore`; undefined when either side missing. */
  scoreDelta?: number | undefined;
  /** ISO-8601 timestamp of the POST scoring; undefined until POST lands. */
  scoredAt?: string | undefined;
  pre?: OnDemandScoreSide | undefined;
  post?: OnDemandScoreSide | undefined;
}

/**
 * One recorded operator override of an enriched attribute — who changed what,
 * and when. Read from the attribute-edits endpoint, which is provenance only:
 * the value the product actually carries comes from the enrichment payload.
 */
export interface ProductAttributeEdit {
  /** Enrichment wire field name — matches `ProductOptimization.field`. */
  field: string;
  /** Value before the edit; `null` when the attribute was previously unset. */
  previousValue: unknown;
  /** Value after the edit; `null` when the operator cleared the attribute. */
  newValue: unknown;
  editedBy: string;
  editedAt: string;
  /** Run the edit was applied to. See `isLiveAttributeEdit`. */
  workflowId: string;
}

// ---------------------------------------------------------------------------
// Catalog Batch Table
// ---------------------------------------------------------------------------

/** Processing status of a catalog batch. `delivering` and `rejected` come from
 *  the decoupled delivery flow — the mapper overlays them onto this display
 *  status from `pipelineStatus` (see `resolveDisplayStatus`). */
export type BatchStatus =
  | 'in-progress'
  | 'delivered'
  | 'failed'
  | 'needs-attention'
  | 'awaiting-approval'
  | 'delivering'
  | 'rejected'
  | 'running'
  | 'completed'
  | 'cancelled';

/** Delivery-lifecycle sub-state from the decoupled delivery flow
 *  (`pipelineStatus`). Distinct from `BatchStatus`: a run stays
 *  `AWAITING_APPROVAL` at the top level while this reads `DELIVERING`.
 *  `(string & {})` tolerates future values without losing autocomplete. */
export type BatchPipelineStatus =
  | 'AWAITING_APPROVAL'
  | 'DELIVERING'
  | 'COMPLETED'
  | 'REJECTED'
  | 'FAILED'
  | (string & {});

/** A single catalog batch row in the batch table. */
export interface BatchRecord {
  /** Job uuid — used as the path param for detail/products endpoints. */
  id: string;
  /** Human-readable batch identifier shown in the Batch ID column. */
  batchId: string;
  /** Human-readable batch name. */
  name: string;
  /** Current processing status. */
  status: BatchStatus;
  /** ISO date string when the batch was created. */
  createdAt: string;
  /** ISO date string when the batch was delivered, or null if not yet delivered. */
  deliveredAt: string | null;
  /** Backend use cases joined for display (e.g. "GMC_ADS, META_FEED"). */
  enrichmentType: string;
  /** Total number of products in the batch. */
  productCount: number;
  /** Source platform name. */
  sourcePlatform: string;
  /** Destination platform name. Backend doesn't yet model this — mirrors source for now. */
  destinationPlatform: string;
  /**
   * Coverage percentage (0–100). Not in the listing payload today —
   * mapper defaults to 0; populated only on the detail page.
   */
  coveragePercent: number;
  /** How the batch was triggered — `'TRIAL'` identifies trial enrichment jobs. */
  source: string;
  /**
   * For `source === 'TRIAL'` rows: the current trial stage
   * (`'CONTENT_SCORING'` | `'SAMPLE_ENRICHMENT'` | `'FULL_CATALOG'` | `'COMPLETED'`).
   * `null` on non-trial rows.
   */
  trialStage: string | null;
  /** trialId for trial enriched batches */
  trialId?: string | null;
  /** Raw decoupled-delivery sub-state. Kept alongside the overlaid `status`
   *  (which already reflects DELIVERING/REJECTED) so callers that need the
   *  distinct signal don't have to re-derive it. `null`/absent on legacy rows. */
  pipelineStatus?: BatchPipelineStatus | null;
  /** Enrichment workflow id driving the batch — the delivery trigger's
   *  `workflowId` (whose enriched output to deliver). `null` on rows without one. */
  workflowId?: string | null;
  /** Raw connector code of the batch's platform (`'GMC' | 'META' | 'FILE' | …`).
   *  Used to gate which delivery destinations a batch may be delivered to. */
  connectorCode?: string | null;
  /** List Archive visibility stamp — set when the row is hidden from the
   *  default list. Derive `archived` from it (`isArchived(row)`); never a
   *  separate boolean. Optional: pre-rollout backends omit it. */
  archivedAt?: string | null;
  /** Who archived it — populated only while archived; `null` on rows archived
   *  before changeset 013 (render nothing, not a placeholder). */
  archivedBy?: string | null;
  /** Who started the batch: an email, or `WORKFLOW-SERVICE` when a schedule's worker did. */
  createdBy?: string | null;
  /** Last human action of any kind, newest wins. Carried by the archive PATCH
   *  response so detail headers can be patched in place without a refetch. */
  lastActionBy?: string | null;
  lastActionAt?: string | null;
  /** What that last action was (null = none / not sent). */
  lastActionType?: LastActionTypeWire | null;
  /** Cost-gate waiting state from the list API; null when none / not sent. */
  costGate?: CostGate | null;
  /** Batch pause / retry fields (contract: batch-retry-contract.md). All optional:
   *  absent on backends that predate it, and the UI then falls back to `status`. */
  batchStatus?: BatchRetryStatus | null;
  retryAttempts?: number | null;
  activeRetryRunId?: string | null;
  pausedAt?: string | null;
  pausedBy?: string | null;
  needsAttention?: number | null;
  productsExcluded?: number | null;
}

/** Mapped page envelope returned by `useBatches` — preserves backend pagination metadata. */
export interface BatchListPage {
  items: BatchRecord[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// ---------------------------------------------------------------------------
// Batch Detail Page
// ---------------------------------------------------------------------------

/** Pipeline stage of a batch (header pill strip). */
export type BatchPipelineStageKey =
  | 'received'
  | 'curated'
  | 'enriched'
  | 'validated'
  | 'delivered'
  | 'verified';

/** A pipeline stage pill with count and active/pending state. */
export interface BatchPipelineStage {
  key: BatchPipelineStageKey;
  label: string;
  /** Products at this stage. `null` means the backend doesn't know yet and must
   *  render as an em dash; `0` is a real answer meaning genuinely none. The two
   *  are deliberately not collapsed — a delivered count of 0 is a common,
   *  legitimate state on runs that completed without pushing a feed. */
  count: number | null;
  /** Controls colors. `failed` is derived in the mapper: when the overall batch
   *  status is failed, the stage that was mid-flight (active) gets flipped to
   *  failed so the UI surfaces the failure where it happened. `skipped` and
   *  `rejected` are terminal — the stage will never run — and are kept separate
   *  from `pending` (still expected to run) so the strip can't imply that work
   *  is still coming. */
  state: 'complete' | 'active' | 'pending' | 'failed' | 'skipped' | 'rejected';
  /** Shown when the stage is not yet reached (e.g. "Scheduled"). */
  pendingLabel?: string;
  /** True when the backend marked this stage as not applicable to the batch
   *  (e.g. connector-specific skip). Carried through so the UI can eventually
   *  distinguish "not yet reached" from "will never run" — not yet consumed
   *  by BatchPipelineStages' rendering. */
  suppressed: boolean;
  /** Stage entered/exited timestamps (ISO-8601), when the backend reports them.
   *  Retained so the coverage phase can measure the enrichment duration off the
   *  Enriched stage without re-reading the header wire. */
  startedAt?: string | null;
  completedAt?: string | null;
}

/** A top-line metric card on the batch detail page (Trends Applied, etc.). */
export interface BatchTopMetric {
  label: string;
  value: string;
  /** Optional small badge shown next to the value. */
  badge?: string;
  /** Optional help text shown in an info-icon tooltip next to the label. */
  tooltip?: string;
}

/** A labelled row in the 3-column Batch Processing Details panel. */
export interface BatchProcessingRow {
  label: string;
  value: string;
  /** When true, renders a chevron indicating a drill-in affordance. */
  drillIn?: boolean;
  /** When true, renders a small info icon next to the label. */
  info?: boolean;
  /** When true, renders a chevron-down instead of chevron-right; wins over drillIn. */
  expandable?: boolean;
  /** When true, suppresses the drill-in click affordance (used when count is 0). */
  disabled?: boolean;
  /** When true, the row is a locked premium feature — implies `disabled` and
   *  renders a premium badge next to the value instead of a drill-in chevron. */
  locked?: boolean;
  /** When set alongside `locked`, shows this text in a tooltip on the premium
   *  badge explaining why the row is locked. */
  lockedTooltip?: string;
}

/** A column in the 3-column Batch Processing Details panel. */
export interface BatchProcessingColumn {
  title: string;
  rows: BatchProcessingRow[];
}

/** Semantic color token for a Needs Attention category — maps to chip + donut-segment styling. */
export type AttentionColor =
  | 'indigo-dark'
  | 'indigo-light'
  | 'sky'
  | 'fuchsia'
  | 'rose'
  | 'emerald';

/** A category row in the Needs Attention breakdown list. */
export interface BatchAttentionItem {
  label: string;
  count: number;
  /** Semantic color token — resolved to Tailwind class and hex in the component. */
  color: AttentionColor;
}

/** A horizontal bar row in the attribute / trend / occasion / keyword lists. */
export interface BatchAttributeRow {
  label: string;
  count: number;
}

/**
 * Phase 1 (shell) — the header slice of the batch detail. Everything the page
 * needs to paint the breadcrumb, title, actions, pipeline timeline, and the
 * derived status flags. Fetched via `GET /jobs/{id}/header`.
 */
export interface BatchHeader {
  batchId: string;
  /** Human-readable batch name. Empty string when the backend hasn't set one. */
  name: string;
  status: BatchStatus;
  /** Display label for the source chip (e.g. `Google Ads`), derived from the
   *  use-case. NOT the raw trigger enum — use `triggerSource` for behavioural
   *  checks like `isFeedAuditSource`. */
  source: string;
  /** Raw enrichment use-case behind the `source` label (`GMC_ADS` | `META_ADS` |
   *  …). Surfaced for use-case-specific UI branches (e.g. the Meta coverage
   *  layout). `null`/absent on legacy rows. */
  useCase?: string | null;
  /** Raw backend trigger enum (`SCHEDULED` | `MANUAL` | `TRIAL` | `FEED_AUDIT`).
   *  Mirrors `BatchRecord.source`; kept distinct from the display `source` above. */
  triggerSource: string;
  createdAt: string;
  /** Workflow-service audit stamps (string actor — email / WORKFLOW-SERVICE /
   *  service token / UNKNOWN / null). Optional; rendered via `ActorStringLabel`. */
  createdBy?: string | null;
  lastActionBy?: string | null;
  lastActionAt?: string | null;
  /** What that last action was (null = none / not sent). */
  lastActionType?: LastActionTypeWire | null;
  /** Targeting filters the batch ran with, normalised to strings / string arrays (the
   *  Batch Info sheet's shape). Absent when the backend sends none. */
  filters?: Record<string, string | string[]>;
  /** The cost the stepper showed at creation; absent when none was stored. */
  plannedCost?: PlannedCost;
  /** Cost-gate waiting state (null = nothing pending, or a backend that predates it). */
  costGate?: CostGate | null;
  /** Batch pause / retry fields (contract: batch-retry-contract.md). All optional:
   *  absent on backends that predate it, and the UI then falls back to `status`. */
  batchStatus?: BatchRetryStatus | null;
  retryAttempts?: number | null;
  activeRetryRunId?: string | null;
  pausedAt?: string | null;
  pausedBy?: string | null;
  productsExcluded?: number | null;
  /** List Archive visibility stamp — drives the header's Archived badge and the
   *  admin Unarchive control. `null`/absent while the batch is active. */
  archivedAt?: string | null;
  /** Who archived it — populated only while archived (changeset 013). */
  archivedBy?: string | null;
  /** Visibility history, newest-first (max 50) — detail payloads only. */
  archiveHistory?: ArchiveHistoryEntry[] | null;
  /** Backend's fine-grained current pipeline stage (e.g. `ENRICHMENT_INSIGHTS`).
   *  Surfaced for the run/batch info sheet. Optional — absent on unstamped rows. */
  currentStage?: string | null;
  /** Total products curated in this batch. Optional — mirrors the wire field;
   *  surfaced for the batch info sheet's product count. */
  productsCuratedTotal?: number | null;
  /** Products per class the create screen offered (`productClasses.atCreation`). */
  productClassesOffered?: Record<string, unknown> | null;
  /** Products per class curation took in (`productClasses.curated[class].included`). */
  productClassesIncluded?: Record<string, number> | null;
  deliveredAt?: string;
  sourcePlatform: string;
  destinationPlatform: string;
  stages: BatchPipelineStage[];
  /** Curation run uuid — used to fetch per-batch products on the products
   *  page. Optional because backend backfill is in progress; the products
   *  page shows the mock dataset until this resolves to a real value. */
  curationRefId?: string;
  /** Import sub-job reference id. Surfaced for the super-admin debug panel; not
   *  used by the main UI. */
  importRefId?: string | null;
  /** Temporal workflow slug for this batch (live polling, Temporal links, cost).
   *  Optional: absent on legacy rows. */
  workflowId?: string;
  /** Workflow whose enriched rows the CSV export reads (`sourceWorkflowId`). The
   *  run that enriched for experiments, else `workflowId`. */
  exportWorkflowId?: string;
  /** Backend connector code (e.g. `GOOGLE_MERCHANT`). Threaded through so
   *  downstream pages can ask `/curation/filters` for the connector-specific
   *  filter set. */
  connectorCode?: string;
  /** True when the backend status is `AWAITING_APPROVAL`. NOTE: since Delivery
   *  Decouple this stays true while a run is DELIVERING (top-level `status`
   *  doesn't change during delivery) — gate the *actionable* approve UI on
   *  `isAwaitingApproval && !isDelivering && !isRejected`. */
  isAwaitingApproval: boolean;
  /** True while `pipelineStatus === 'DELIVERING'` — the approved run is actively
   *  delivering. Drives the "Delivering" chip and disables the Review action. */
  isDelivering: boolean;
  /** True when the run was rejected at the approval gate (terminal, not Failed).
   *  There is no in-place un-reject — the path forward is a fresh run. */
  isRejected: boolean;
  /** Raw decoupled-delivery sub-state, when the backend emits it. */
  pipelineStatus?: BatchPipelineStatus | null;
  /** Integration delivery-run id — used as `run_id` for optional live delivery
   *  progress. `undefined`/`null` until pushed and for mock/suppressed runs. */
  deliveryJobId?: string | null;
  /** True when the backend `source` is `'TRIAL'`. Trial batches don't get a
   *  real delivery step — combined with `isAwaitingApproval`, this drives the
   *  "Completed" status override and the locked/premium Delivery stage. */
  isTrial: boolean;
  /** Run configuration the batch was created with, when the backend echoes it.
   *  Feeds the delivery panel's attribute picker so it offers only what this run
   *  enriched. `undefined` on batches created before the echo — the panel then
   *  falls back to the full taxonomy. */
  runConfig?: EnrichmentRunConfig;
  /** Every language the run delivers, `en-US` first. `['en-US']` for an English-only run and
   *  for any batch from before translation existed. */
  locales: string[];
  /** Attributes the agent produces regardless of `runConfig.contentAttributes`
   *  (Meta only) — from the echoed `enrichmentConfig`. Keeps those fields in the
   *  delivery picker even though they weren't requested. `undefined` on Google and
   *  on batches created before the echo. */
  alwaysOnAttributes?: string[];
  /** Whether the run enabled the search-descriptors step (from the echoed
   *  `enrichmentConfig`). `false` gates the Search Descriptors tab off; `null`/
   *  absent (not applicable / not echoed) leaves it enabled. */
  searchDescriptorsEnabled?: boolean | null;
  /** Whether the run enabled the phrase-descriptions step (from the echoed
   *  `enrichmentConfig`). Used alongside `searchDescriptorsEnabled` to hide the
   *  disabled steps' rows from the /coverage cards. `null`/absent = unknown. */
  phraseDescriptionsEnabled?: boolean | null;
  /** The delivery pipeline's own workflow id — the target for the delivery-
   *  approval endpoint. Distinct from `batchId` (the `scheduled-job-…` slug).
   *  `undefined` on legacy runs; the approve action is inert without it. */
  pipelineWorkflowId?: string | null;
}

/**
 * Phase 3 (coverage tabs) — the Delivery Health + Enrichment Coverage slice.
 * Several of these derivations depend on header fields (status, trial flag, and
 * the Enriched stage's timestamps), so the mapper takes a {@link BatchHeader}.
 * Fetched via `GET /jobs/{id}/coverage`.
 */
export interface BatchCoverageDetail {
  processing: BatchProcessingColumn[];
  attention: {
    total: number;
    items: BatchAttentionItem[];
  };
  attributes: BatchAttributeRow[];
  trends: BatchAttributeRow[];
  /** Occasion coverage rows — only populated when the API returns this data. */
  occasions: BatchAttributeRow[];
  /** Top keyword rows — only populated when the API returns this data. */
  keywords: BatchAttributeRow[];
  enrichmentCoverage: EnrichmentCoverage;
  performanceSignals: PerformanceSignals;
  /** Raw `deliveryHealth.productsEnriched`. Also rendered (formatted) in the
   *  `processing` columns, but carried as a number so the products page can
   *  compare it against the products list's own newly-enriched count and surface
   *  a gap. `undefined` when the payload omits `deliveryHealth`. */
  productsEnriched?: number | undefined;
}

/**
 * Full detail payload powering the (legacy single-call) Batch Details page.
 * Composed from the phase slices so the aggregate can never drift from the
 * split; `optimizationQuality` is its own phase (`GET /jobs/{id}/optimization-quality`).
 */
export interface BatchDetail extends BatchHeader, BatchCoverageDetail {
  optimizationQuality: OptimizationQuality;
}

/** Phase 2 (live progress) — the refined pipeline timeline from
 *  `GET /jobs/{id}/live-stage`. `null` at the hook layer when the endpoint
 *  answers `204` (terminal/unreachable), so callers fall back to
 *  `header.stages`. */
export interface BatchLiveStage {
  /** Live current stage reported by the running workflow; null when the run is
   *  parked at its cost gate with no live stage to report. */
  currentStage: string | null;
  /** Per-stage snapshots, mapped to the same shape as `header.stages` so the
   *  pipeline component consumes either interchangeably. */
  stages: BatchPipelineStage[];
  /** The run's pending cost gate (null = none, or a backend that does not send it). */
  costGate: CostGate | null;
}

// ---------------------------------------------------------------------------
// Performance Signals tab
// ---------------------------------------------------------------------------

/** A single row for the Popular Product Terms table.
 *  Shape matches AIPerformanceInsight for structural compatibility. */
export interface PerformanceTermItem {
  term: string;
  monthlySearchCount: number;
  impressionShare: { beforePercent: string; afterPercent: string };
  terms: { beforeCount: number; afterCount: number };
  /** Keyword Planner competition band + index (0–100). `null` when Google
   *  returns neither for the term. */
  competition: { level: 'low' | 'medium' | 'high'; index: number } | null;
}

/** A single row for the Popular Product Attributes list.
 *  Shape matches AttributeItem for structural compatibility.
 *  `beforeCount/afterCount` are the raw product counts behind the scores —
 *  null on runs computed before the counts shipped, and absent entirely on the
 *  job-detail path (`tagCoverageToAttributes`), which only has percentages. */
export interface PerformanceAttributeItem {
  title: string;
  beforeScore: number;
  afterScore: number;
  beforeCount?: number | null;
  afterCount?: number | null;
  /** Monthly search volume for this attribute. Null when Google has none. */
  monthlySearches?: number | null;
  /** Product-category slug, or `null` for a batch-wide row. */
  productCategory?: string | null;
  /** The row's own `sampleSize` from the wire. */
  sampleSize?: number | null;
}

/** A single before/after comparison row for occasions and trends.
 *  Shape matches AttributeComparisonItem for structural compatibility.
 *  `trendKind` tags merged micro/macro trend rows so the UI can badge them. */
export interface PerformanceCoverageItem {
  title: string;
  beforeValue: number;
  afterValue: number;
  trendKind?: 'micro' | 'macro';
}

/** Aggregate data for the Performance Signals batch detail tab. */
export interface PerformanceSignals {
  /** Rows for the AIPerformanceInsights Popular Product Terms table. */
  popularTerms: PerformanceTermItem[];
  /** Rows for the AttributeScoreList Popular Product Attributes section. */
  popularAttributes: PerformanceAttributeItem[];
  /** Rows for Occasions Coverage AttributeComparisonList. */
  occasions: PerformanceCoverageItem[];
  /** Upper bound for occasion bar widths (100 for percentages). */
  occasionsMax: number;
  /** Rows for Top Trends AttributeComparisonList. */
  trends: PerformanceCoverageItem[];
  /** Upper bound for trend bar widths (max count across all trends). */
  trendsMax: number;
  /** Before/after summary KPIs for the top-4 cards. Null until the job produces data. */
  kpis: PerformanceKpis | null;
}

/** One before/after pair for a Performance Insights summary card. */
export interface BeforeAfterCount {
  before: number;
  after: number;
}

/** Before/after summary KPIs on the Performance Insights step. */
export interface PerformanceKpis {
  /** The only remaining before/after KPI. `keywordsMatched`, `monthlySearchVolume`
   *  and `highVolumeProducts` were removed from the job-detail response by
   *  gap_analysis_v3 — declaring them made the UI dereference absent fields. */
  occasionsCovered: BeforeAfterCount;
}

/** Performance Signals tab data sourced from `GET enrichments/{workflowId}/insights`.
 *  Same shape as {@link PerformanceSignals} minus `kpis` (that endpoint doesn't
 *  return it), plus `trendsAppliedCount`, `searchTermsCoveredCount`, and
 *  `occasionTagsAppliedCount`. */
export interface BatchInsights extends Omit<PerformanceSignals, 'kpis'> {
  /** Sum of TOP_TRENDS `countAfter` across all trend rows — total trend-tag
   *  applications after enrichment. Powers the batch detail page's "Trends
   *  Applied" top metric card. */
  trendsAppliedCount: number | null;
  /** Sum of PRODUCT_TERM `countAfter` across all term rows — total
   *  search-term matches after enrichment. Powers the batch detail page's
   *  "Search Terms Covered" top metric card. */
  searchTermsCoveredCount: number | null;
  /** Sum of OCCASIONS_COVERAGE `countAfter` across all occasion rows — total
   *  occasion-tag applications after enrichment. Powers the batch detail
   *  page's "Occasion Tags Applied" top metric card.
   *
   *  All three are `null` when their insight rows are empty (signal not
   *  computed for this batch); the card renders that as "—", a real 0 as 0. */
  occasionTagsAppliedCount: number | null;
  /** Cohort size behind the Popular Product Attributes counts, derived rather
   *  than sent. Undefined when it can't be recovered (empty cohort, or a run
   *  that predates the counts) — the caller then omits the caption. */
  attributeSampleSize?: number;
  /** Popular Product Attributes categories with their product counts
   *  (`POPULAR_ATTRIBUTE_CATEGORIES`). Absent on backends that predate it. */
  attributeCategorySizes?: AttributeCategorySize[];
}

/** Tab value for the Delivery Health / Performance Signals / Enrichment Coverage / Optimization Quality
 *  switcher, plus Run details (Batch Details Funnel, shown only behind the `batch_funnel` gate). */
export type BatchDetailTab =
  | 'delivery-health'
  | 'performance-signals'
  | 'enrichment-coverage'
  | 'optimization-quality'
  | 'run-details'
  /** Batch pause / retry: root run + retry children (Runs timeline). */
  | 'runs';

// ---------------------------------------------------------------------------
// Enrichment Coverage tab
// ---------------------------------------------------------------------------

/** Color tone for a coverage percent — data-driven, not computed from thresholds. */
export type CoverageTone = 'green' | 'orange' | 'red';

/** A single stat card in the Enrichment Coverage summary row. */
export interface CoverageStat {
  label: string;
  value: string;
  /** Optional help text shown in an info-icon tooltip next to the label. */
  tooltip?: string;
  /** Optional small line below the value (e.g. "92% coverage", "Started on: 25 Mar, 26"). */
  sub?: { text: string; tone: 'positive' | 'neutral' };
  /** True for the "Processing Since" label while a batch is still running —
   *  colors the label text instead of the standard muted color. */
  isInProgress?: boolean;
}

/** A row in the Category Level Coverage list — label, bar, percent. */
export interface CategoryCoverageRow {
  label: string;
  percent: number;
  tone: CoverageTone;
}

/** A row in the Attribute Level Coverage list — adds a current/total count column. */
export interface AttributeCoverageRow {
  label: string;
  current: number;
  total: number;
  percent: number;
  tone: CoverageTone;
}

/** Full payload for the Enrichment Coverage tab. */
export interface EnrichmentCoverage {
  stats: CoverageStat[];
  categories: CategoryCoverageRow[];
  attributes: AttributeCoverageRow[];
}

// ---------------------------------------------------------------------------
// Enrichment Coverage signals — GET enrichments/{workflowId}/coverage
// ---------------------------------------------------------------------------

/** The four product-detail change states, in display order. */
export type ProductDetailState = 'added' | 'changed' | 'removed' | 'renamed';

/** A coverage-by-category row: how much of one product category came back
 *  enriched. `percent` is of that category's *attempted* products, failures
 *  included, so it can sit below 100 even when nothing errored visibly. */
export interface CoverageCategoryRow {
  label: string;
  /** Products in this category that enriched successfully. */
  enriched: number;
  percent: number;
}

/** A coverage-by-attribute row: products with a populated value, before vs
 *  after. Monotone upstream by construction — `after < before` is a data
 *  integrity signal, not a legitimate regression, which is why `isRegression`
 *  exists as an explicit flag rather than being derived at render time. */
export interface CoverageAttributeRow {
  label: string;
  beforeCount: number;
  afterCount: number;
  beforePercent: number;
  afterPercent: number;
  /** True when `afterCount < beforeCount` — should never happen; surfaced so
   *  the UI can flag corrupt data instead of quietly rendering it. */
  isRegression: boolean;
}

/** An attribute-depth row. Counts are **items** (total array/JSONB elements
 *  across the batch), not products, and `avgPerProductAfter` is average items
 *  per product — unbounded, never a percentage. A negative `delta` is expected
 *  and is at most an investigate signal. */
export interface CoverageDepthRow {
  label: string;
  itemsBefore: number;
  itemsAfter: number;
  delta: number;
  avgPerProductBefore: number;
  avgPerProductAfter: number;
}

/** A product-detail change row: how many products gained/lost/changed one
 *  attribute. `percent` is of the diffable cohort, not the whole batch. */
export interface CoverageDetailRow {
  label: string;
  products: number;
  percent: number;
}

/** Batch-scoped coverage cards, sourced from
 *  `GET enrichments/{workflowId}/coverage` on workflow-service. Distinct from
 *  {@link EnrichmentCoverage}, which the job-detail endpoint supplies for the
 *  summary tiles and the two coverage lists already on this tab. */
export interface BatchCoverage {
  categories: CoverageCategoryRow[];
  attributes: CoverageAttributeRow[];
  depth: CoverageDepthRow[];
  /** Upper bound for depth bar widths — the largest `itemsAfter` in the set,
   *  or 1 when empty so a bar never divides by zero. */
  depthMax: number;
  productDetail: Record<ProductDetailState, CoverageDetailRow[]>;
}

// ---------------------------------------------------------------------------
// Optimization Quality tab
// ---------------------------------------------------------------------------

/** A single row in a Quality Score sub-panel (label + before/after score). */
export interface QualityScoreRow {
  label: string;
  /** Pre-enrichment score on the 0–10 wire scale. `null` when the attribute was
   *  never graded at PRE — distinct from a real 0.0, which means the agent
   *  graded it and it scored zero. */
  beforeScore: number | null;
  /** Post-enrichment score on the 0–10 wire scale, e.g. 6.28. `null` when POST
   *  never re-graded this attribute: the agent grades a wider set at PRE than at
   *  POST, so a row can legitimately carry a before and no after. */
  score: number | null;
}

/** A labelled sub-panel inside the Quality Score card. */
export interface QualityScorePanel {
  title: string;
  subtitle: string;
  rows: QualityScoreRow[];
}

/** Before/after scores for the Sample Content Quality Score card. */
export interface SampleQualityScore {
  beforeScore: number;
  afterScore: number;
}

/** Full payload for the Optimization Quality tab. */
export interface OptimizationQuality {
  sampleQualityScore: SampleQualityScore;
  qualityScore: {
    title: string;
    subtitle: string;
    panels: QualityScorePanel[];
  };
  /** Before/after/lift scores for the five RD3C dimensions (correctness,
   *  completeness, compliance, relevance, differentiation). */
  dimensionScores: DimensionScore[];
  /** Products in the curated pre-enrichment scoring sample; null when the run
   *  didn't sample. Drives the "Based on the N-product sample" note. */
  curatedPreScoredCount: number | null;
}

// ---------------------------------------------------------------------------
// Needs Attention drawer
// ---------------------------------------------------------------------------

/** Severity of a single attention alert — drives dot + pill color. */
export type AttentionAlertSeverity = 'alert' | 'warning' | 'info';

/** Tone for a metric value displayed inside an alert's panel. */
export type AttentionMetricTone = 'neutral' | 'positive' | 'negative';

/** A single labelled metric column inside an alert panel (e.g. "8.2% Control OOS"). */
export interface AttentionMetric {
  label: string;
  value: string;
  tone?: AttentionMetricTone;
}

/** Optional metric panel shown below an alert's description (label + severity pill + metric columns). */
export interface AttentionAlertPanel {
  title: string;
  severity: AttentionAlertSeverity;
  metrics: AttentionMetric[];
}

/** Optional info banner shown at the bottom of an alert (rendered with an info icon). */
export interface AttentionAlertInfo {
  text: string;
}

/** A single attention alert within a category group. */
export interface AttentionAlert {
  id: string;
  title: string;
  severity: AttentionAlertSeverity;
  description: string;
  panel?: AttentionAlertPanel;
  info?: AttentionAlertInfo;
}

/** A collapsible group of alerts in the Needs Attention drawer (e.g. "Women's Footwear — Title + Description"). */
export interface AttentionCategoryGroup {
  id: string;
  title: string;
  alerts: AttentionAlert[];
}

/** Props for NeedsAttentionDrawer. */
export interface NeedsAttentionDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A tab entry in the batch table tab bar. */
export interface BatchTab {
  /** Unique tab value used by the Radix Tabs primitive. */
  value: string;
  /** Human-readable label shown on the tab. */
  label: string;
  /** Count badge displayed next to the label. Undefined for tabs that don't
   *  map to a `JobStatusCounts` field (hides the pill). */
  count?: number;
  /** Status filter applied when this tab is active. Undefined means "all". */
  statusFilter?: BatchStatus;
}

/** Mapped per-status job counts — UI shape returned by `useJobStatusCounts`.
 *  Fields mirror `JobStatusCountsResponse` verbatim (identical shape); kept
 *  as its own type so components never import the wire type directly. */
export interface JobStatusCounts {
  all: number;
  pending: number;
  inProgress: number;
  /** Excludes delivering rows since Delivery Decouple — smaller than before. */
  awaitingApproval: number;
  /** Approved runs actively delivering (from `pipeline_status`). Optional until
   *  the backend rollout completes — treat a missing value as 0. */
  delivering?: number;
  /** Runs turned down at the approval gate (terminal, not Failed). Optional until
   *  the backend rollout completes — treat a missing value as 0. */
  rejected?: number;
  completed: number;
  failed: number;
  reset: number;
  /** Runs the Stop button killed (`status = ARCHIVED`) — NOT the List Archive
   *  flag. Optional until the backend rollout completes. */
  stopped?: number;
  /** @deprecated Alias of `stopped`. Never use it for the Archived-view badge —
   *  that is `all` from a `?archived=ARCHIVED` request. */
  archived: number;
}

// ---------------------------------------------------------------------------
// Workflow retry-from-step (batch detail)
// ---------------------------------------------------------------------------

/** Which recovery paths #2 accepts for a given failure point. `RESET` rewinds
 *  and re-runs from `resetToEventId`; `RESUME_REMAINING` (scheduled-pipeline
 *  `STAGE:ENRICHMENT` points only) enriches just the missing products, then
 *  continues to scoring/approval/delivery under a new run of the same
 *  `workflowId`. */
export type WorkflowRetryMode = 'RESET' | 'RESUME_REMAINING';

/** A single retryable failure point in a workflow's Temporal history —
 *  `resetToEventId` is the `WorkflowTaskCompleted` event to resume from. */
export interface WorkflowFailurePoint {
  activityId: string;
  activityType: string;
  failureEventId: number;
  failedAt: string;
  attempt: number;
  failureMessage: string;
  resetToEventId: number;
  resetToDescription: string;
  /** Modes #2 accepts for this point. Always contains `RESET`; missing on an
   *  older backend, in which case callers must treat it as `['RESET']`. */
  retryModes?: WorkflowRetryMode[];
}

/** Response from `GET .../workflows/{workflowId}/failure-points`. */
export interface WorkflowFailurePointsResponse {
  workflowId: string;
  runId: string;
  workflowType: string;
  status: string;
  /** Normalised to `[]` by the service — the wire sends `null` when history is gone. */
  failurePoints: WorkflowFailurePoint[];
  /** `false` once the run is past Temporal retention: no reset point can be
   *  read, so retry is impossible and `reason` says why. Absent on older backends. */
  historyAvailable?: boolean;
  reason?: string | null;
}

/** Response from `POST .../workflows/{workflowId}/retry`. */
export interface WorkflowRetryResponse {
  newRunId: string;
  /** Present on a current backend; a request that omitted `mode` implies `RESET`. */
  mode?: WorkflowRetryMode;
}
